import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ReportBuilder } from "@/components/ReportBuilder";
import { ReportCharts } from "@/components/ReportCharts";
import { ScheduledReportsManager } from "@/components/ScheduledReportsManager";
import { EmailTemplateSettings } from "@/components/EmailTemplateSettings";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { FileText, Trash2, Download, BarChart3, Eye, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ReportPreviewDialog } from "@/components/ReportPreviewDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface SavedReport {
  id: string;
  name: string;
  description: string | null;
  report_type: string;
  is_public: boolean;
  created_at: string;
  tables: string[];
  columns: Record<string, string[]>;
}

export default function Reports() {
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCharts, setShowCharts] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<SavedReport | null>(null);

  useEffect(() => {
    fetchSavedReports();
  }, []);

  const fetchSavedReports = async () => {
    try {
      const { data, error } = await supabase
        .from("custom_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSavedReports((data || []) as SavedReport[]);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast({ title: "Failed to fetch reports", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      const { error } = await supabase.from("custom_reports").delete().eq("id", reportId);

      if (error) throw error;

      toast({ title: "Report deleted" });
      fetchSavedReports();
    } catch (error) {
      console.error("Error deleting report:", error);
      toast({ title: "Failed to delete report", variant: "destructive" });
    }
  };

  const handlePreviewReport = (report: SavedReport) => {
    setSelectedReport(report);
    setPreviewOpen(true);
  };

  const handleExportReport = async (report: SavedReport, format: "pdf" | "excel") => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      toast({ title: `Generating ${format.toUpperCase()} export...` });

      const response = await fetch(`https://jogykhlglxdcsqjrlmag.supabase.co/functions/v1/export-report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reportId: report.id,
          reportName: report.name,
          format,
          tables: report.tables,
          columns: report.columns,
        }),
      });

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.name}_${Date.now()}.${format === "excel" ? "csv" : "html"}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({ title: `${format.toUpperCase()} exported successfully` });
    } catch (error) {
      console.error("Error exporting:", error);
      toast({ title: "Export failed", variant: "destructive" });
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Reports & Analytics</h1>
          <p className="text-muted-foreground">Create custom reports, view analytics, and schedule automated reports</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="show-charts" className="text-sm">
            Show Charts
          </Label>
          <Switch id="show-charts" checked={showCharts} onCheckedChange={setShowCharts} />
        </div>
      </div>

      {/* Analytics Charts */}
      {showCharts && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Analytics Overview</h2>
          </div>
          <ReportCharts />
        </div>
      )}

      {/* Tabs for different sections */}
      <Tabs defaultValue="reports" className="space-y-6">
        <TabsList>
          <TabsTrigger value="reports">Report Builder</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Reports</TabsTrigger>
          <TabsTrigger value="saved">Saved Reports</TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="w-4 h-4 mr-2" />
            Email Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports">
          <ReportBuilder onReportCreated={fetchSavedReports} />
        </TabsContent>

        <TabsContent value="scheduled">
          <ScheduledReportsManager />
        </TabsContent>

        <TabsContent value="saved">
          <Card>
            <CardHeader>
              <CardTitle>Saved Reports</CardTitle>
              <CardDescription>Your previously saved custom reports</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center text-muted-foreground py-8">Loading reports...</p>
              ) : savedReports.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No saved reports yet. Create your first report above.
                </p>
              ) : (
                <div className="grid gap-4">
                  {savedReports.map((report) => (
                    <Card key={report.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <h3 className="font-semibold">{report.name}</h3>
                              {report.is_public && <Badge variant="secondary">Public</Badge>}
                            </div>
                            {report.description && (
                              <p className="text-sm text-muted-foreground mb-2">{report.description}</p>
                            )}
                            <div className="flex flex-wrap gap-1">
                              {report.tables.map((table) => (
                                <Badge key={table} variant="outline" className="text-xs">
                                  {table}
                                </Badge>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Created: {new Date(report.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button size="sm" variant="outline" onClick={() => handlePreviewReport(report)}>
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleExportReport(report, "excel")}>
                              <Download className="w-4 h-4 mr-1" />
                              Excel
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleExportReport(report, "pdf")}>
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteReport(report.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <EmailTemplateSettings />
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      {selectedReport && (
        <ReportPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          reportName={selectedReport.name}
          tables={selectedReport.tables}
          columns={selectedReport.columns}
          onExport={(format) => {
            handleExportReport(selectedReport, format);
            setPreviewOpen(false);
          }}
        />
      )}
    </div>
  );
}
